/** XeAgents.ai **/
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Phone,
  PhoneOff,
  Voicemail,
  UserCheck,
  Users,
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
  ArrowRight,
  RefreshCw,
  MessageSquare,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useState } from "react";
import { formatDate, formatRelativeTime } from "@/lib/date-format";

interface JourneyContact {
  id: string;
  contactId: string;
  contactName?: string;
  contactPhone?: string;
  currentState: string;
  stateAttemptCount: number;
  persistedData: Record<string, any>;
  stateHistory: Array<{
    from: string;
    to: string;
    timestamp: string;
    trigger: string;
    callId?: string;
  }>;
  nextCallAt: string | null;
  nextFlowId: string | null;
  isComplete: boolean;
  finalOutcome: string | null;
  createdAt: string;
  updatedAt: string;
}

interface JourneyStats {
  totalContacts: number;
  byState: Record<string, number>;
  completed: number;
  inProgress: number;
  successfulOutcomes: number;
  nextScheduledCalls: number;
}

interface ContactJourneyTimelineProps {
  campaignId: string;
}

// State color mapping
const stateColors: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
  dial: { bg: "bg-blue-100", text: "text-blue-700", icon: <Phone className="w-4 h-4" /> },
  initial_dial: { bg: "bg-blue-100", text: "text-blue-700", icon: <Phone className="w-4 h-4" /> },
  gatekeeper: { bg: "bg-orange-100", text: "text-orange-700", icon: <Users className="w-4 h-4" /> },
  gatekeeper_bypass: { bg: "bg-purple-100", text: "text-purple-700", icon: <Users className="w-4 h-4" /> },
  dm_pitch: { bg: "bg-green-100", text: "text-green-700", icon: <UserCheck className="w-4 h-4" /> },
  decision_maker: { bg: "bg-green-100", text: "text-green-700", icon: <UserCheck className="w-4 h-4" /> },
  voicemail: { bg: "bg-sky-100", text: "text-sky-700", icon: <Voicemail className="w-4 h-4" /> },
  voicemail_followup: { bg: "bg-sky-100", text: "text-sky-700", icon: <Voicemail className="w-4 h-4" /> },
  booking: { bg: "bg-emerald-100", text: "text-emerald-700", icon: <Calendar className="w-4 h-4" /> },
  callback: { bg: "bg-amber-100", text: "text-amber-700", icon: <Clock className="w-4 h-4" /> },
  closed: { bg: "bg-gray-100", text: "text-gray-700", icon: <CheckCircle2 className="w-4 h-4" /> },
  completed: { bg: "bg-emerald-100", text: "text-emerald-700", icon: <CheckCircle2 className="w-4 h-4" /> },
  failed: { bg: "bg-red-100", text: "text-red-700", icon: <XCircle className="w-4 h-4" /> },
  dnc: { bg: "bg-red-100", text: "text-red-700", icon: <PhoneOff className="w-4 h-4" /> },
};

function getStateStyle(state: string) {
  const normalized = state.toLowerCase().replace(/[\s-]/g, "_");
  return stateColors[normalized] || { bg: "bg-gray-100", text: "text-gray-700", icon: <Phone className="w-4 h-4" /> };
}

function formatStateName(state: string): string {
  return state
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

export function ContactJourneyTimeline({ campaignId }: ContactJourneyTimelineProps) {
  const [selectedJourney, setSelectedJourney] = useState<JourneyContact | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Fetch journey stats
  const { data: stats, isLoading: statsLoading } = useQuery<JourneyStats>({
    queryKey: [`/api/state-machine/campaigns/${campaignId}/journey-stats`],
    enabled: !!campaignId,
  });

  // Fetch all journeys
  const { data: journeys = [], isLoading: journeysLoading } = useQuery<JourneyContact[]>({
    queryKey: [`/api/state-machine/campaigns/${campaignId}/journeys`],
    enabled: !!campaignId,
  });

  const toggleExpand = (journeyId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(journeyId)) {
        next.delete(journeyId);
      } else {
        next.add(journeyId);
      }
      return next;
    });
  };

  if (statsLoading || journeysLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-96 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Contacts</p>
              <p className="text-2xl font-bold">{stats?.totalContacts || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100">
              <RefreshCw className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">In Progress</p>
              <p className="text-2xl font-bold">{stats?.inProgress || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-100">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Completed</p>
              <p className="text-2xl font-bold">{stats?.completed || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-100">
              <Calendar className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Scheduled Calls</p>
              <p className="text-2xl font-bold">{stats?.nextScheduledCalls || 0}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* State Distribution */}
      {stats?.byState && Object.keys(stats.byState).length > 0 && (
        <Card className="p-4">
          <h3 className="font-semibold mb-3">Contacts by State</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.byState).map(([state, count]) => {
              const style = getStateStyle(state);
              return (
                <Badge
                  key={state}
                  variant="secondary"
                  className={`${style.bg} ${style.text} gap-1`}
                >
                  {style.icon}
                  {formatStateName(state)}: {count}
                </Badge>
              );
            })}
          </div>
        </Card>
      )}

      {/* Journey List */}
      <Card>
        <div className="p-4 border-b">
          <h3 className="font-semibold">Contact Journeys</h3>
          <p className="text-sm text-muted-foreground">
            Track each contact's progress through the campaign
          </p>
        </div>

        {journeys.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No journeys started yet</p>
            <p className="text-sm">Journeys will appear here once calls are made</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Current State</TableHead>
                <TableHead>Attempts</TableHead>
                <TableHead>Next Call</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {journeys.map((journey) => {
                const style = getStateStyle(journey.currentState);
                const isExpanded = expandedRows.has(journey.id);

                return (
                  <>
                    <TableRow
                      key={journey.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => toggleExpand(journey.id)}
                    >
                      <TableCell>
                        <Button variant="ghost" size="sm" className="p-0 h-6 w-6">
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{journey.contactName || "Unknown"}</p>
                          <p className="text-sm text-muted-foreground">{journey.contactPhone}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${style.bg} ${style.text} gap-1`}>
                          {style.icon}
                          {formatStateName(journey.currentState)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{journey.stateAttemptCount}</span>
                        <span className="text-muted-foreground"> / state</span>
                      </TableCell>
                      <TableCell>
                        {journey.nextCallAt ? (
                          <div className="text-sm">
                            <p className="font-medium">
                              {formatDate(journey.nextCallAt, "MMM d, h:mm a")}
                            </p>
                            <p className="text-muted-foreground">
                              {formatRelativeTime(journey.nextCallAt)}
                            </p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {journey.isComplete ? (
                          <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            {journey.finalOutcome || "Complete"}
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                            <RefreshCw className="w-3 h-3 mr-1" />
                            In Progress
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Expanded Timeline */}
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={6} className="bg-muted/30 p-4">
                          <div className="space-y-4">
                            {/* Persisted Variables */}
                            {journey.persistedData && Object.keys(journey.persistedData).length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold mb-2">Captured Information</h4>
                                <div className="flex flex-wrap gap-2">
                                  {Object.entries(journey.persistedData)
                                    .filter(([key]) => !key.startsWith("_"))
                                    .map(([key, value]) => (
                                      <Badge key={key} variant="outline">
                                        <span className="text-muted-foreground mr-1">{key}:</span>
                                        {String(value)}
                                      </Badge>
                                    ))}
                                </div>
                              </div>
                            )}

                            {/* State History Timeline */}
                            <div>
                              <h4 className="text-sm font-semibold mb-2">Journey Timeline</h4>
                              <div className="relative pl-6 space-y-3">
                                {journey.stateHistory.map((event, idx) => {
                                  const fromStyle = getStateStyle(event.from);
                                  const toStyle = getStateStyle(event.to);
                                  const isLast = idx === journey.stateHistory.length - 1;

                                  return (
                                    <div key={idx} className="relative">
                                      {/* Timeline line */}
                                      {!isLast && (
                                        <div className="absolute left-[-18px] top-6 w-0.5 h-full bg-gray-200" />
                                      )}
                                      {/* Timeline dot */}
                                      <div className="absolute left-[-22px] top-1 w-2 h-2 rounded-full bg-primary" />

                                      <div className="bg-white rounded-lg p-3 border shadow-sm">
                                        <div className="flex items-center gap-2 mb-1">
                                          <Badge className={`${fromStyle.bg} ${fromStyle.text} text-xs`}>
                                            {formatStateName(event.from)}
                                          </Badge>
                                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                                          <Badge className={`${toStyle.bg} ${toStyle.text} text-xs`}>
                                            {formatStateName(event.to)}
                                          </Badge>
                                        </div>
                                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                          <span>{formatDate(event.timestamp, "MMM d, h:mm a")}</span>
                                          <span className="text-primary">Trigger: {event.trigger}</span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}

                                {/* Current State */}
                                <div className="relative">
                                  <div className="absolute left-[-22px] top-1 w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                                  <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                                    <div className="flex items-center gap-2">
                                      <Badge className={`${getStateStyle(journey.currentState).bg} ${getStateStyle(journey.currentState).text}`}>
                                        {getStateStyle(journey.currentState).icon}
                                        {formatStateName(journey.currentState)}
                                      </Badge>
                                      <span className="text-sm text-amber-700">Current State</span>
                                    </div>
                                    {journey.nextCallAt && (
                                      <p className="text-xs text-muted-foreground mt-1">
                                        Next call: {formatDate(journey.nextCallAt, "EEEE, MMM d 'at' h:mm a")}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
